import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  get,
  set,
  size,
  find,
  pick,
  flow,
  slice,
  reject,
  isNaN,
  isEmpty
} from 'lodash/fp';
import {
  FormGroup,
  Button,
  ButtonToolbar,
  FormControl,
  ControlLabel,
  RadioGroup,
  Radio,
  Input,
  InputNumber,
  Modal
} from 'rsuite';
import { parseInt } from '../../../helpers/utils';
import { LimitRulesItemType } from '../../../interfaces/reservation';
import {
  StyledModal,
  FormHeader,
  StyledForm,
  StyledFormGroup,
  BreakdownSection,
  QtyHeader
} from './elements';

const RulesKey = { HH: 'HH', NR: 'NR' };
const HouseholdRulesTypes = { DEFAULT: '1', BREAKDOWN: '2' };
const DefaultBreakdown = [
  { key: `1-3${RulesKey.HH}`, quantity: 1, from: 1, to: 3 },
  { key: `4+${RulesKey.HH}`, quantity: 2, from: 4, to: '+' }
];

const composeBreakdownError = breakdown =>
  breakdown.reduce((result, each) => {
    const sizeError = each.to < each.from;
    if (sizeError) return set(each.key, { size: sizeError }, result);

    return result;
  }, {});

const LimitRulesModal = ({ show, rules, onCancel, onSave }) => {
  const [householdRuleType, setHouseholdRuleType] = useState(
    HouseholdRulesTypes.DEFAULT
  );
  const isByHouseholdBreakdown = useMemo(
    () => householdRuleType === HouseholdRulesTypes.BREAKDOWN,
    [householdRuleType]
  );

  const [noResidenceQty, setNoResidenceQty] = useState(1);
  const [householdQty, setHouseholdQty] = useState(1);
  const [householdBreakdown, setHouseholdBreakdown] = useState(
    DefaultBreakdown
  );
  const [formError, setFormError] = useState({});

  const handleNoResidenceQtyChange = value => {
    setNoResidenceQty(value);
  };

  const handleHouseholdQtyChange = value => {
    setHouseholdQty(value);
  };

  const handleHouseholdRuleTypeChange = value => {
    setHouseholdRuleType(value);
  };

  const handleBreakdownChange = i => value => {
    const updatedBreakdown = set(`${i}.to`, value, householdBreakdown);
    setHouseholdBreakdown(updatedBreakdown);
  };

  const handleBreakdownQtyChange = i => value => {
    const intValue = parseInt(value);
    const quantity = !isNaN(intValue) ? intValue : value;
    setHouseholdBreakdown(set(`${i}.quantity`, quantity, householdBreakdown));
  };

  const handleUpdateBreakdown = i => ({ target: { value } }) => {
    const item = householdBreakdown[i];
    const nextItemIndex = i + 1;
    const lastItemIndex = size(householdBreakdown) - 1;
    if (lastItemIndex === i) {
      const { from, to, quantity } = householdBreakdown[lastItemIndex];
      const intTo = parseInt(to);
      if (to && intTo >= from) {
        const newBreakdown = {
          key: `${intTo + 1}+${RulesKey.HH}`,
          quantity: parseInt(quantity) + 1,
          from: intTo + 1,
          to: '+'
        };
        const updatedBreakdown = set(
          `${lastItemIndex}.key`,
          `${from}-${to}+${RulesKey.HH}`,
          householdBreakdown
        );
        setHouseholdBreakdown([...updatedBreakdown, newBreakdown]);
      }
    } else if (value === '+') {
      const updatedBreakdown = set(
        `${i}.key`,
        `${item.from}+${RulesKey.HH}`,
        householdBreakdown
      );
      setHouseholdBreakdown(slice(0, nextItemIndex, updatedBreakdown));
    } else if (
      value &&
      nextItemIndex <= lastItemIndex &&
      item.to + 1 !== householdBreakdown[nextItemIndex].from
    ) {
      const itemKey = `${item.from}-${item.to}${RulesKey.HH}`;
      const nextItem = householdBreakdown[nextItemIndex];
      const nextItemFrom = parseInt(value) + 1;
      const nextItemKey =
        nextItem.to === '+'
          ? `${nextItemFrom}+${RulesKey.HH}`
          : `${nextItemFrom}-${nextItem.to}${RulesKey.HH}`;

      const updatedBreakdown = flow(
        set(`${i}.key`, itemKey),
        set(`${nextItemIndex}.key`, nextItemKey),
        set(`${nextItemIndex}.from`, nextItemFrom)
      )(householdBreakdown);

      setHouseholdBreakdown(updatedBreakdown);
    }
  };

  useEffect(
    () => {
      if (!rules) return;
      const noResidenceRules = find({ key: RulesKey.NR }, rules);
      const defaultHouseholdRules = find({ key: RulesKey.HH }, rules);

      setNoResidenceQty(noResidenceRules.quantity);
      if (defaultHouseholdRules) {
        setHouseholdRuleType(HouseholdRulesTypes.DEFAULT);
        setHouseholdQty(defaultHouseholdRules.quantity);
      } else {
        setHouseholdRuleType(HouseholdRulesTypes.BREAKDOWN);
        setHouseholdBreakdown(reject({ key: RulesKey.NR }, rules));
      }
    },
    [rules]
  );

  useEffect(
    () => {
      setFormError({
        ...(pick(RulesKey.NR, formError) || {}),
        ...composeBreakdownError(householdBreakdown)
      });
    },
    [householdBreakdown]
  );

  const handleSave = () =>
    onSave([
      { key: RulesKey.NR, quantity: parseInt(noResidenceQty) },
      ...(!isByHouseholdBreakdown
        ? [{ key: RulesKey.HH, quantity: parseInt(householdQty) }]
        : householdBreakdown)
    ]);

  return (
    <StyledModal show={show} onClose={onCancel}>
      <Modal.Header>
        <Modal.Title>Items Limit Rules</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledForm layout="horizontal">
          <FormHeader>
            Allowed Items Quantity for:
            <QtyHeader>Items Qty</QtyHeader>
          </FormHeader>
          <StyledFormGroup>
            <ControlLabel>No Residence:</ControlLabel>
            <FormControl
              accepter={InputNumber}
              value={noResidenceQty}
              onChange={handleNoResidenceQtyChange}
              className={
                get(`${RulesKey.NR}.quantity`, formError) ? 'error' : ''
              }
            />
          </StyledFormGroup>
          <RadioGroup
            name="householdRuleType"
            value={householdRuleType}
            onChange={handleHouseholdRuleTypeChange}
          >
            <Radio value={HouseholdRulesTypes.DEFAULT} inline>
              By Household:
            </Radio>
            <InputNumber
              value={householdQty}
              disabled={isByHouseholdBreakdown}
              onChange={handleHouseholdQtyChange}
              className={
                get(`${RulesKey.HH}.quantity`, formError) ? 'error' : ''
              }
            />
            <p className="or">or</p>
            <Radio value={HouseholdRulesTypes.BREAKDOWN} inline>
              By Household Size:
            </Radio>
          </RadioGroup>
          <BreakdownSection className={!isByHouseholdBreakdown && 'disabled'}>
            <QtyHeader>Items Qty</QtyHeader>
            {householdBreakdown.map((each, i) => (
              <FormGroup key={each.key}>
                <ControlLabel>{each.from} to</ControlLabel>
                <FormControl
                  accepter={Input}
                  value={each.to}
                  disabled={!isByHouseholdBreakdown}
                  className={get(`${each.key}.size`, formError) ? 'error' : ''}
                  onChange={handleBreakdownChange(i)}
                  onBlur={handleUpdateBreakdown(i)}
                />
                <ControlLabel>Individual:</ControlLabel>
                <FormControl
                  accepter={InputNumber}
                  value={each.quantity}
                  disabled={!isByHouseholdBreakdown}
                  onChange={handleBreakdownQtyChange(i)}
                  className={
                    get(`${each.key}.quantity`, formError) ? 'error' : ''
                  }
                />
              </FormGroup>
            ))}
          </BreakdownSection>
        </StyledForm>
      </Modal.Body>
      <Modal.Footer>
        <ButtonToolbar>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            appearance="primary"
            onClick={handleSave}
            disabled={!isEmpty(formError)}
          >
            Save
          </Button>
        </ButtonToolbar>
      </Modal.Footer>
    </StyledModal>
  );
};

LimitRulesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  rules: PropTypes.arrayOf(LimitRulesItemType)
};

LimitRulesModal.defaultProps = {
  rules: null
};

export default LimitRulesModal;
